import {
	MentionNodeDataProvider,
	type FetchMentionNodeData,
	type ResolveMentionNodeData,
} from '@atlaskit/editor-plugin-mentions/mention-node-data-provider';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { getRovoChatIdentityFromCache } from '@confluence/fabric-media-support/entry-points/preloadRovoChatIdentity.ts';
import { cfetch } from '@confluence/network';
import { getCloudId, getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

type MentionAvatarData = NonNullable<ReturnType<ResolveMentionNodeData>>;

const IDENTITY_USER_ARI_PREFIX = 'ari:cloud:identity::user/';

const getAvatarUrl = (accountId: string): string =>
	`/wiki/aa-avatar/${encodeURIComponent(accountId)}`;

const normalizeIdentityAccountId = (accountId: string): string =>
	accountId.startsWith(IDENTITY_USER_ARI_PREFIX)
		? accountId.slice(IDENTITY_USER_ARI_PREFIX.length)
		: accountId;

const isAgentMention = ({ userType }: Parameters<ResolveMentionNodeData>[0]): boolean =>
	userType === 'APP' || userType === 'AGENT';

const createMentionAvatarData = (accountId: string, isAgent: boolean): MentionAvatarData => {
	const avatarData: MentionAvatarData = {
		appType: isAgent ? 'agent' : 'user',
		avatarUrl: getAvatarUrl(accountId),
		isAvatarImagePreShaped: isAgent
			? !expVal('platform_editor_drop3_hexagon_agent_avatar', 'isEnabled', false)
			: true,
	};
	if (expValEquals('platform_editor_mention_rovo', 'isEnabled', true)) {
		const cloudId = getCloudId();
		return {
			...avatarData,
			isRovoChat: !!cloudId && getRovoChatIdentityFromCache(cloudId) === accountId,
		};
	}
	return avatarData;
};

const createMentionAvatarDataMap = (
	accountIds: string[],
	isAgent = false,
): Record<string, MentionAvatarData> =>
	Object.fromEntries(
		accountIds.map((accountId) => [accountId, createMentionAvatarData(accountId, isAgent)]),
	);

const resolveMentionNodeData: ResolveMentionNodeData = (mention): MentionAvatarData | undefined => {
	if (!mention.userType) {
		return undefined;
	}

	return createMentionAvatarData(mention.id, isAgentMention(mention));
};

const resolveIsAgentByAccountId = async (
	accountId: string,
	cloudId: string,
	signal?: AbortSignal,
): Promise<boolean> => {
	try {
		const response = await cfetch(
			`/gateway/api/assist/rovo/v1/agents/accountid/${normalizeIdentityAccountId(accountId)}`,
			{
				method: 'GET',
				headers: {
					'x-product': 'confluence',
					'x-experience-id': 'ai-mate',
					'x-cloudid': cloudId,
				},
				signal,
			},
		);
		const agent = await response.json();

		return typeof agent?.id === 'string' && agent.id !== '';
	} catch (error: unknown) {
		if (signal?.aborted) {
			throw error;
		}

		return false;
	}
};

const fetchMentionNodeData: FetchMentionNodeData = async (
	accountIds,
	signal,
): Promise<Record<string, MentionAvatarData>> => {
	let cloudId: string;
	try {
		cloudId = (await getSessionData()).cloudId;
	} catch {
		return createMentionAvatarDataMap(accountIds);
	}

	const entries = await Promise.all(
		accountIds.map(
			async (accountId): Promise<[string, MentionAvatarData]> => [
				accountId,
				createMentionAvatarData(
					accountId,
					await resolveIsAgentByAccountId(accountId, cloudId, signal),
				),
			],
		),
	);

	return Object.fromEntries(entries);
};

/** Creates a page-scoped provider backed by Confluence's deterministic avatar route. */
export const createMentionNodeDataProvider = (): MentionNodeDataProvider =>
	new MentionNodeDataProvider(fetchMentionNodeData, resolveMentionNodeData);
